'use client';

import { MantineProvider, createTheme } from '@mantine/core';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import { AuthProvider } from '@/contexts/auth';
import { ApiProvider } from '@/contexts/api';
import { CurrentUserProvider } from '@/contexts/current-user';
import { ModelsProvider } from '@/contexts/models';
import { SubscriptionProvider } from '@/contexts/subscription';
import { TeamsProvider } from '@/contexts/teams';
import { TagsProvider } from '@/contexts/tags';
import { AuthModalProvider, MessageModalProvider, ModalStackProvider } from '@/contexts/layout';
import { TeamModalHost, TeamModalProvider } from '@/contexts/team-modal';
import { SessionStartChooserHost, SessionStartChooserProvider } from '@/contexts/session-start-chooser';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { TweaksProvider } from '@/components/dev-tools/tweaks2';
import { TeamsGalleryTweaksHost } from './home-page/TeamsGalleryTweaksHost';
import { CookiesProvider } from '@/contexts/cookies';
import { SessionsListProvider } from '@/contexts/sessions-list';
import { useBlurOnOutsidePointer, useFocusScrollDebug } from '@/hooks/dom';
import { useSeqLog, LogLevel } from '@/hooks/telemetry';
import { getFontFamily } from '@/theme';

// Diagnostic-only hook: pays per-focus cost (getBoundingClientRect + getComputedStyle
// on up to 12 ancestors × 7 timed snapshots) regardless of whether Seq ingest is enabled,
// because snapshot arguments are evaluated before the noop logger is reached. Restrict to
// development so production interaction paths stay cheap. Module-level conditional is
// rules-of-hooks safe because process.env.NODE_ENV is constant per bundle.
const NOOP_HOOK: () => void = () => {};
const useFocusScrollDebugIfDev: () => void =
  process.env.NODE_ENV === 'development' ? useFocusScrollDebug : NOOP_HOOK;

const LANDING_PERF_BOOT_COMPONENT = 'LandingPerf.Boot';
const LANDING_PERF_BOOT_MESSAGE = 'providers-mounted';

const THEME_PRIMARY_COLOR = 'cabal';
const CABAL_THEME_COLORS = [
  '#e6fff9',
  '#ccfff3',
  '#99ffe7',
  '#66feda',
  '#33fed1',
  '#00fec8',
  '#66FEC1',
  '#00cc9f',
  '#009975',
  '#00664c',
] as const;

const theme = createTheme({
  primaryColor: THEME_PRIMARY_COLOR,
  fontFamily: getFontFamily(),
  colors: {
    cabal: CABAL_THEME_COLORS,
  },
});

export function Providers({ children }: { children: ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000, // 1 minute
            refetchOnWindowFocus: false,
          },
        },
      })
  );

  useBlurOnOutsidePointer();
  useFocusScrollDebugIfDev();

  const logBoot = useSeqLog(LANDING_PERF_BOOT_COMPONENT);
  useEffect(() => {
    logBoot(LANDING_PERF_BOOT_MESSAGE, {
      nodeEnv: process.env.NODE_ENV,
      providersMountedMs: performance.now(),
      navigationStartMs: performance.timeOrigin,
      visibilityState: typeof document !== 'undefined' ? document.visibilityState : null,
      dpr: typeof window !== 'undefined' ? window.devicePixelRatio : null,
      innerWidth: typeof window !== 'undefined' ? window.innerWidth : null,
      innerHeight: typeof window !== 'undefined' ? window.innerHeight : null,
    }, LogLevel.Info);
  }, [logBoot]);

  return (
      <QueryClientProvider client={queryClient}>
        <CookiesProvider>
          <MantineProvider theme={theme}>
            <ApiProvider>
              <AuthProvider>
                <CurrentUserProvider>
                  <SubscriptionProvider>
                    <TeamsProvider>
                      <TagsProvider>
                        <ModelsProvider>
                          <SessionsListProvider>
                            <ModalStackProvider>
                              <MessageModalProvider>
                                <AuthModalProvider>
                                  <TeamModalProvider>
                                    <SessionStartChooserProvider>
                                      <ErrorBoundary>
                                        {/* Dev-only tweaks v2 provider — renders children bare in production. */}
                                        <TweaksProvider>
                                          {children}
                                          {/* The ONE team-modal mount. Under the gallery
                                              tweaks owner so the panel's design axes reach
                                              the modal from every origin (production builds
                                              resolve to the locked bundle either way). */}
                                          <TeamsGalleryTweaksHost>
                                            <TeamModalHost />
                                          </TeamsGalleryTweaksHost>
                                          {/* The ONE session-start chooser mount — its own
                                              tweaks context, so it sits beside the gallery
                                              tweaks owner rather than under it. */}
                                          <SessionStartChooserHost />
                                        </TweaksProvider>
                                      </ErrorBoundary>
                                    </SessionStartChooserProvider>
                                  </TeamModalProvider>
                                </AuthModalProvider>
                              </MessageModalProvider>
                            </ModalStackProvider>
                          </SessionsListProvider>
                        </ModelsProvider>
                      </TagsProvider>
                    </TeamsProvider>
                  </SubscriptionProvider>
                </CurrentUserProvider>
              </AuthProvider>
            </ApiProvider>
          </MantineProvider>
        </CookiesProvider>
      </QueryClientProvider>
  );
}
